import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import {useParams, useSearchParams} from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import RouteContainer from '../../../../common/styles/RouteContainer'
import RouteTitle from '../../../../common/RouteTitle/RouteTitle'
import booksitoutIcon from '../../../../config/BooksitoutIcon'
import RouteTitleConfig from '../../../../config/RouteTitleConfig'
import RowSpacer from '../../../../common/styles/RowSpacer'
import ScrollToTop from '../../../../common/ScrollToTop'
import {BooksitoutServer} from '../../../../config/BooksitoutServer'
import BookResponse from '../../BookResponse'
import BookMineDoneCard from '../../mine/BookMineDoneCard'
import BookMineCard from '../../mine/BookMineCard'
import NoContentForFullScreen from '../../../../common/NoContentForFullScreen'
import useHtmlTitle from '../../../../common/useHtmlTitle'
import BookMineLoadingCard from '../../mine/BookMineLoadingCard'
import type BookStatus from '../../detail/BookStatusType'
import breakpoints from '../../../../config/Breakpoints'
import {BookLanguage, getBookLanguageDisplayName} from '../../BookType'
import useLanguageReadingSpeed from '../hooks/useLanguageReadingSpeed'
import {formatSpeedComparison, getNativePagesPerHour, getSpeedTone, SpeedTone} from '../responses/LanguageReadingSpeedResponse'

const STATUS_OPTIONS: { value: BookStatus | 'ALL'; label: string }[] = [
	{ value: 'ALL', label: '전체' },
	{ value: 'NOT_STARTED', label: '시작 전' },
	{ value: 'READING', label: '읽는 중' },
	{ value: 'DONE', label: '다 읽음' },
	{ value: 'GIVE_UP', label: '포기' },
]

const BookStatisticsLanguageBooksRoute = () => {
	const {language} = useParams<{ language: string }>()
	const [searchParams, setSearchParams] = useSearchParams()
	const languageName = language === 'UNKNOWN' ? '알 수 없음' : getBookLanguageDisplayName((language ?? 'UNKNOWN') as BookLanguage)

	useHtmlTitle(`${languageName} | 언어별 독서`)

	const [books, setBooks] = useState<BookResponse[]>([])
	const [totalCount, setTotalCount] = useState<number>(0)
	const [isLoading, setIsLoading] = useState(true)
	const [page, setPage] = useState(1)
	const [isLast, setIsLast] = useState(false)

	const statusParam = searchParams.get('status') as BookStatus | null
	const statusFilter: BookStatus | 'ALL' = statusParam && STATUS_OPTIONS.some(o => o.value === statusParam) ? statusParam : 'ALL'
	const size = 32

	useEffect(() => {
		if (!language) return

		const statusParam = statusFilter !== 'ALL' ? `&status=${statusFilter}` : ''
		BooksitoutServer
			.get(`/v1/statistics/languages/${language}/books?page=${page}&size=${size}${statusParam}`)
			.then((res) => {
				setBooks(prev => page === 1 ? res.data.content : [...prev, ...res.data.content])
				setTotalCount(res.data.totalElements ?? 0)
				setIsLast(res.data.last ?? true)
			})
			.catch(() => setBooks([]))
			.finally(() => setIsLoading(false))
	}, [language, page, statusFilter])

	const handleStatusChange = (status: BookStatus | 'ALL') => {
		if (status === 'ALL') {
			searchParams.delete('status')
		} else {
			searchParams.set('status', status)
		}
		setSearchParams(searchParams, {replace: true})
		setPage(1)
		setBooks([])
		setIsLoading(true)
	}

	const fetchNext = () => setPage(prev => prev + 1)

	const [readingSpeed] = useLanguageReadingSpeed()
	const languageSpeed = readingSpeed?.languages.find(l => l.language === language)
	const averagePagesPerHour = readingSpeed?.averagePagesPerHour

	const native = languageSpeed?.native ?? null

	const comparePercent = native?.differencePercent ?? languageSpeed?.differencePercent ?? 0


	return (
		<RouteContainer>
			<ScrollToTop/>
			<RouteTitle
				icon={<booksitoutIcon.statistics/>}
				title={'통계'}
				subTitle={'내 독서활동에 대한 통계를 확인할 수 있어요'}
				currentKey={'statistics'}
				buttons={RouteTitleConfig.Book}
				rightUi={undefined}
			/>

			<RowSpacer size={40}/>

			<ListHeader>
				<LanguageName>{languageName}</LanguageName>
				{!isLoading && (
					<CountIndicator>{totalCount}권</CountIndicator>
				)}
			</ListHeader>

			{languageSpeed && averagePagesPerHour != null && (
				<SpeedCard>
					<SpeedTitle>읽기 속도</SpeedTitle>
					<SpeedMainRow>
						<SpeedMainValue>시간당 {Math.round(languageSpeed.pagesPerHour)}쪽</SpeedMainValue>
						<DiffValue $tone={getSpeedTone(comparePercent)}>{formatSpeedComparison(comparePercent)}</DiffValue>
					</SpeedMainRow>
					<SpeedSubValue>
						{native
							? `원어민 평균 시간당 ${Math.round(getNativePagesPerHour(native))}쪽`
							: `내 전체 평균 시간당 ${Math.round(averagePagesPerHour)}쪽`}
					</SpeedSubValue>
				</SpeedCard>
			)}

			<RowSpacer size={10}/>

			{(
				<>
					<StatusFilterRow>
						{STATUS_OPTIONS.map(opt => (
							<StatusFilterButton
								key={opt.value}
								$active={statusFilter === opt.value}
								onClick={() => handleStatusChange(opt.value)}
							>
								{opt.label}
							</StatusFilterButton>
						))}
					</StatusFilterRow>
					<RowSpacer size={10}/>
				</>
			)}

			{isLoading ? (
				<Row>
					{Array.from({length: 12}).map((_, i) => (
						<Col key={i}>
							<BookMineLoadingCard/>
						</Col>
					))}
				</Row>
			) : books.length === 0 ? (
				<NoContentForFullScreen message={statusFilter !== 'ALL' ? '해당 상태의 책이 없어요' : `${languageName}로 된 책이 없어요`}/>
			) : (
				<InfiniteScroll
					loader={<Row>{Array.from({length: 12}).map((_, i) => <Col key={i}><BookMineLoadingCard/></Col>)}</Row>}
					next={fetchNext}
					hasMore={!isLast}
					dataLength={books.length}
					className="overflow-hidden"
				>
					<Row>
						{books.map(book => (
							<Col key={book.id}>
								{book.status === 'DONE' ? (
									<BookMineDoneCard book={book}/>
								) : (
									<BookMineCard book={book}/>
								)}
							</Col>
						))}
					</Row>
				</InfiniteScroll>
			)}
		</RouteContainer>
	)
}

const Row = styled.div.attrs({
	className: 'row',
})``

const ListHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 8px;
`

const LanguageName = styled.h4`
	font-weight: bold;
	color: var(--text-color);
	margin: 0;
`

const CountIndicator = styled.h5`
	font-weight: bold;
	color: var(--primary-color);
	margin: 0;
`

const SpeedCard = styled.div`
	margin: 15px 8px 0;
	padding: 16px 20px;
	border-radius: 16px;
	background-color: var(--card-bg-color);
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
`

const SpeedTitle = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: var(--sub-text-color);
	margin-bottom: 4px;
`

const SpeedMainRow = styled.div`
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 10px;
`

const SpeedMainValue = styled.span`
	font-size: 18px;
	font-weight: 600;
	color: var(--text-color);
`

const SpeedSubValue = styled.div`
	margin-top: 2px;
	font-size: 13px;
	color: var(--sub-text-color);
`

const DiffValue = styled.span<{ $tone: SpeedTone }>`
	font-weight: 600;
	color: ${props => props.$tone === 'faster' ? 'var(--primary-color)' : props.$tone === 'slower' ? '#e5534b' : 'var(--sub-text-color)'};
`

const StatusFilterRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	flex-wrap: wrap;
	padding: 0 8px;
	margin-top: 10px;
`

const StatusFilterButton = styled.button<{ $active: boolean }>`
	padding: 6px 16px;
	border-radius: 20px;
	border: none;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	background-color: ${props => props.$active ? 'var(--primary-color)' : 'var(--search-field-bg-color)'};
	color: ${props => props.$active ? 'white' : 'var(--text-color)'};

	@media (max-width: ${breakpoints.md}) {
		width: calc((100% - 16px) / 3);
		text-align: center;
	}

	&:hover {
		opacity: 0.85;
	}
`

const Col = styled.div.attrs({
	className: 'col-6 col-md-4 col-lg-3 col-xl-2',
})`
	margin-top: 30px;
	margin-bottom: 30px;
	height: 300px;

	& > a {
		display: block;
		height: 100%;
	}

	.clickable {
		height: 100%;
		padding-top: 5px;
		padding-bottom: 5px;
	}
`

export default BookStatisticsLanguageBooksRoute
